import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { BuyButton } from "@/components/store/BuyButton";
import { PRODUCT } from "@/constants/product";

const LINES = [["Use what", "you have."], ["Plan what", "you need."], ["Track what", "you spend."]];
const ease = [0.16, 1, 0.3, 1];

const MaskLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "105%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay: 0.25 + i * 0.12, ease }}>
      {children}
    </motion.span>
  </span>
);

const Badge = () => (
  <motion.svg viewBox="0 0 120 120" className="absolute -left-10 -top-8 z-20 h-28 w-28 sm:h-32 sm:w-32" animate={{ rotate: 360 }} transition={{ duration: 22, repeat: Infinity, ease: "linear" }} aria-hidden="true">
    <defs><path id="c" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0" /></defs>
    <circle cx="60" cy="60" r="58" fill="#c8452c" />
    <text fill="#fbf8f1" fontSize="10" fontWeight="700" letterSpacing="2.2"><textPath href="#c">INSTANT PDF · PRINT AT HOME ·</textPath></text>
  </motion.svg>
);

const BadgeCenter = () => (
  <div className="pointer-events-none absolute -left-10 -top-8 z-30 flex h-28 w-28 items-center justify-center sm:h-32 sm:w-32">
    <span className="font-display text-2xl text-[var(--paper)]" data-testid="hero-price-badge">{PRODUCT.price}</span>
  </div>
);

const Cover = ({ mx, my, y }) => {
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 110, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-13, 13]), { stiffness: 110, damping: 16 });
  return (
    <motion.div style={{ y }} className="relative mx-auto w-[78%] max-w-[400px] [perspective:1200px] lg:w-full">
      <motion.div initial={{ opacity: 0, rotateY: -35, y: 60 }} animate={{ opacity: 1, rotateY: 0, y: 0 }} transition={{ duration: 1.4, delay: 0.4, ease }}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <img src="/assets/ws-weekly-meal-plan.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full rounded-sm border border-black/15 object-cover shadow-xl" style={{ transform: "translateZ(-60px) translateX(12%) rotate(6deg)" }} />
        <img src={PRODUCT.cover} alt="Workbook cover: Meal Planning and Grocery Systems" data-testid="hero-cover-image"
          className="relative w-full rounded-sm border border-black/25 shadow-[0_50px_80px_-30px_rgba(23,20,15,0.6)]" style={{ transform: "translateZ(40px)" }} />
        <div style={{ transform: "translateZ(90px)" }} className="absolute inset-0"><Badge /><BadgeCenter /></div>
      </motion.div>
    </motion.div>
  );
};

export const KineticHero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const move = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
    ref.current.style.setProperty("--sx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--sy", `${e.clientY - r.top}px`);
  };
  return (
    <section id="top" ref={ref} onMouseMove={move} className="spotlight relative overflow-hidden px-5 pb-24 pt-32 sm:px-10 lg:pt-40" data-testid="hero-section">
      <div className="relative mx-auto grid max-w-[1360px] items-center gap-16 lg:grid-cols-[1.45fr_0.55fr]">
        <motion.div style={{ y: textY }}>
          <MaskLine i={0} className="mb-8 text-xs font-semibold uppercase tracking-[0.22em] text-[var(--sage-deep)]">
            <span data-testid="hero-eyebrow">The Meal Planning &amp; Grocery Systems Workbook</span>
          </MaskLine>
          <h1 className="font-display text-[2.7rem] leading-[0.95] sm:text-6xl lg:text-[5.4rem] xl:text-[6.2rem]" data-testid="hero-title">
            {LINES.map(([a, b], i) => (
              <MaskLine key={a} i={i + 1}>{a} <span className={i === 1 ? "italic text-[var(--sage-deep)]" : ""}>{b}</span></MaskLine>
            ))}
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9 }} className="mt-10 grid max-w-2xl gap-8 sm:grid-cols-[1fr_auto] sm:items-end">
            <p className="text-base text-[var(--muted)] md:text-lg" data-testid="hero-subtitle">{PRODUCT.subtitle}</p>
            <BuyButton label={`Get the workbook · ${PRODUCT.price}`} testId="hero-buy-button" />
          </motion.div>
        </motion.div>
        <Cover mx={mx} my={my} y={y} />
      </div>
    </section>
  );
};
